'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ShieldCheck, UserPlus, Settings2, Lock, ChevronRight, ChevronLeft, Check, ArrowRight, Users, Crown, Briefcase, Mail, X, Plus, ToggleLeft, ToggleRight, Building2, Globe, Clock, Bell,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';



interface Step {
  id: number;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const steps: Step[] = [
  {
    id: 1,
    title: 'Role Assignment',
    subtitle: 'Define your admin role and access scope',
    icon: Crown,
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10 border-amber-400/20',
  },
  {
    id: 2,
    title: 'Invite Team Members',
    subtitle: 'Add colleagues and assign their roles',
    icon: UserPlus,
    color: 'text-primary',
    bgColor: 'bg-primary/10 border-primary/20',
  },
  {
    id: 3,
    title: 'Workspace Configuration',
    subtitle: 'Set up your workspace preferences',
    icon: Settings2,
    color: 'text-cyan',
    bgColor: 'bg-cyan/10 border-cyan/20',
  },
  {
    id: 4,
    title: 'Permission Levels',
    subtitle: 'Configure access controls for your team',
    icon: Lock,
    color: 'text-success',
    bgColor: 'bg-success/10 border-success/20',
  },
];

interface RoleOption {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  permissions: string[];
  color: string;
}

const roleOptions: RoleOption[] = [
  {
    id: 'super-admin',
    label: 'Super Admin',
    description: 'Full platform access with billing and user management',
    icon: Crown,
    permissions: ['All modules', 'Billing & payments', 'User management', 'System settings'],
    color: 'amber',
  },
  {
    id: 'operations-admin',
    label: 'Operations Admin',
    description: 'Manage projects, teams, and operational workflows',
    icon: Briefcase,
    permissions: ['Projects & BPO', 'Team management', 'Reports & analytics', 'Client portal'],
    color: 'blue',
  },
  {
    id: 'support-admin',
    label: 'Support Admin',
    description: 'Handle tickets, client communications, and escalations',
    icon: Users,
    permissions: ['Support tickets', 'Client portal', 'VOIP & calls', 'Knowledge base'],
    color: 'cyan',
  },
];

interface InviteRow {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'sent';
}

interface PermissionRow {
  module: string;
  admin: boolean;
  manager: boolean;
  employee: boolean;
  client: boolean;
}

const defaultPermissions: PermissionRow[] = [
  { module: 'Dashboard & Analytics', admin: true, manager: true, employee: false, client: false },
  { module: 'Projects & Portfolio', admin: true, manager: true, employee: true, client: true },
  { module: 'Client Management', admin: true, manager: true, employee: false, client: false },
  { module: 'Invoices & Payments', admin: true, manager: false, employee: false, client: true },
  { module: 'Support Tickets', admin: true, manager: true, employee: true, client: true },
  { module: 'Employee Records', admin: true, manager: true, employee: false, client: false },
  { module: 'System Settings', admin: true, manager: false, employee: false, client: false },
  { module: 'Reports & Exports', admin: true, manager: true, employee: false, client: false },
];

export default function AdminOnboardingPage() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState<string>('super-admin');
  const [invites, setInvites] = useState<InviteRow[]>([
    { id: '1', email: '', role: 'employee', status: 'pending' },
  ]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('employee');
  const [workspaceConfig, setWorkspaceConfig] = useState({
    orgName: 'Arrows Groups',
    timezone: 'UTC+5:30',
    language: 'English',
    notifications: true,
    weeklyReports: true,
    twoFactor: false,
  });
  const [permissions, setPermissions] = useState<PermissionRow[]>(defaultPermissions);

  const totalSteps = steps.length;
  const progressPct = ((currentStep - 1) / (totalSteps - 1)) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleFinish = () => {
    router.push('/');
  };

  const addInviteRow = () => {
    if (!newEmail.trim()) return;
    setInvites((prev) => [
      ...prev,
      { id: Date.now().toString(), email: newEmail.trim(), role: newRole, status: 'sent' },
    ]);
    setNewEmail('');
    setNewRole('employee');
  };

  const removeInvite = (id: string) => {
    setInvites((prev) => prev.filter((r) => r.id !== id));
  };

  const togglePermission = (moduleIdx: number, role: keyof Omit<PermissionRow, 'module'>) => {
    setPermissions((prev) =>
      prev.map((row, i) =>
        i === moduleIdx ? { ...row, [role]: !row[role] } : row
      )
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-card/50 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <AppLogo size={30} />
          <span className="font-semibold text-foreground tracking-tight">Arrows</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Step {currentStep} of {totalSteps}</span>
          <span className="hidden sm:inline">— Admin Setup</span>
        </div>
        <button
          onClick={handleFinish}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip setup
        </button>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left sidebar — step tracker */}
        <aside className="hidden lg:flex flex-col w-72 border-r border-border bg-card/30 p-6 flex-shrink-0">
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground">Admin Setup</h2>
            <p className="text-sm text-muted-foreground mt-1">Complete these steps to configure your workspace</p>
          </div>

          <div className="space-y-2 flex-1">
            {steps.map((step) => {
              const Icon = step.icon;
              const isCompleted = step.id < currentStep;
              const isActive = step.id === currentStep;
              return (
                <button
                  key={step.id}
                  onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-200 ${
                    isActive
                      ? 'bg-primary/10 border border-primary/20'
                      : isCompleted
                      ? 'bg-success/5 border border-success/10 cursor-pointer hover:bg-success/10' :'opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isCompleted
                        ? 'bg-success/15 border border-success/25'
                        : isActive
                        ? step.bgColor + 'border' :'bg-muted border border-border'
                    }`}
                  >
                    {isCompleted ? (
                      <Check size={14} className="text-success" />
                    ) : (
                      <Icon size={14} className={isActive ? step.color : 'text-muted-foreground'} />
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isActive ? 'text-foreground' : isCompleted ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.subtitle}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Progress */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Progress</span>
              <span>{Math.round(progressPct)}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {/* Mobile step indicator */}
          <div className="lg:hidden px-4 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-3">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    step.id <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Step {currentStep} of {totalSteps}: <span className="text-foreground font-medium">{steps[currentStep - 1].title}</span>
            </p>
          </div>

          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
            {/* Step header */}
            <div className="mb-8 animate-fade-in">
              {(() => {
                const step = steps[currentStep - 1];
                const Icon = step.icon;
                return (
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${step.bgColor}`}>
                      <Icon size={22} className={step.color} />
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-semibold text-foreground">{step.title}</h1>
                      <p className="text-sm text-muted-foreground mt-0.5">{step.subtitle}</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── STEP 1: Role Assignment ── */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-slide-up">
                <p className="text-sm text-muted-foreground">
                  Select the admin role that best matches your responsibilities. This determines your default access scope across the platform.
                </p>
                <div className="space-y-3">
                  {roleOptions.map((role) => {
                    const Icon = role.icon;
                    const isSelected = selectedRole === role.id;
                    const colorMap: Record<string, string> = {
                      amber: 'border-amber-400/40 bg-amber-400/5',
                      blue: 'border-primary/40 bg-primary/5',
                      cyan: 'border-cyan/40 bg-cyan/5',
                    };
                    const iconColorMap: Record<string, string> = {
                      amber: 'text-amber-400',
                      blue: 'text-primary',
                      cyan: 'text-cyan',
                    };
                    return (
                      <button
                        key={role.id}
                        onClick={() => setSelectedRole(role.id)}
                        className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                          isSelected
                            ? colorMap[role.color]
                            : 'border-border bg-card hover:border-border/80 hover:bg-card/80'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-current/10' : 'bg-muted'}`}>
                            <Icon size={16} className={isSelected ? iconColorMap[role.color] : 'text-muted-foreground'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-sm font-semibold ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                                {role.label}
                              </span>
                              {isSelected && (
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                  <Check size={11} className="text-white" />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2.5">
                              {role.permissions.map((perm) => (
                                <span
                                  key={perm}
                                  className="text-2xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border"
                                >
                                  {perm}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="p-3.5 rounded-xl bg-info/5 border border-info/15 flex gap-3">
                  <ShieldCheck size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Role assignments can be changed later in <span className="text-foreground font-medium">Settings → User Management</span>. Your initial role sets default permissions that can be customized in Step 4.
                  </p>
                </div>
              </div>
            )}

            {/* ── STEP 2: Invite Team Members ── */}
            {currentStep === 2 && (
              <div className="space-y-5 animate-slide-up">
                <p className="text-sm text-muted-foreground">
                  Invite colleagues to join your workspace. They'll receive an email with setup instructions and their assigned role.
                </p>

                {/* Add invite row */}
                <div className="card-surface p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Add team member</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        placeholder="colleague@arrows.io"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addInviteRow()}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      className="sm:w-36 px-3 py-2 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                      <option value="client">Client</option>
                    </select>
                    <button
                      onClick={addInviteRow}
                      className="flex items-center justify-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <Plus size={14} />
                      <span>Add</span>
                    </button>
                  </div>
                </div>

                {/* Invite list */}
                {invites.filter((r) => r.email).length > 0 && (
                  <div className="card-surface divide-y divide-border overflow-hidden">
                    <div className="px-4 py-2.5 bg-muted/30">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Pending Invitations ({invites.filter((r) => r.email).length})
                      </p>
                    </div>
                    {invites.filter((r) => r.email).map((row) => (
                      <div key={row.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-primary">
                            {row.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{row.email}</p>
                          <p className="text-xs text-muted-foreground capitalize">{row.role}</p>
                        </div>
                        <span className={`text-2xs px-2 py-0.5 rounded-full font-medium ${
                          row.status === 'sent' ?'bg-success/10 text-success border border-success/20' :'bg-warning/10 text-warning border border-warning/20'
                        }`}>
                          {row.status === 'sent' ? 'Invite sent' : 'Pending'}
                        </span>
                        <button
                          onClick={() => removeInvite(row.id)}
                          className="p-1 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {invites.filter((r) => r.email).length === 0 && (
                  <div className="card-surface p-8 text-center">
                    <Users size={28} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No invitations added yet</p>
                    <p className="text-xs text-muted-foreground mt-1">You can skip this step and invite members later from Settings</p>
                  </div>
                )}

                <div className="p-3.5 rounded-xl bg-cyan/5 border border-cyan/15 flex gap-3">
                  <Bell size={16} className="text-cyan flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Invitees will receive a welcome email with a secure link to set up their account. Links expire after <span className="text-foreground font-medium">72 hours</span>.
                  </p>
                </div>
              </div>
            )}

            {/* ── STEP 3: Workspace Configuration ── */}
            {currentStep === 3 && (
              <div className="space-y-5 animate-slide-up">
                <p className="text-sm text-muted-foreground">
                  Configure your workspace settings. These preferences apply globally and can be updated anytime in Settings.
                </p>

                <div className="card-surface divide-y divide-border overflow-hidden">
                  {/* Org name */}
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <Building2 size={15} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Organisation Name</p>
                        <p className="text-xs text-muted-foreground">Displayed across the platform</p>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={workspaceConfig.orgName}
                      onChange={(e) => setWorkspaceConfig((p) => ({ ...p, orgName: e.target.value }))}
                      className="sm:w-48 px-3 py-2 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>

                  {/* Timezone */}
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center flex-shrink-0">
                        <Clock size={15} className="text-cyan" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Default Timezone</p>
                        <p className="text-xs text-muted-foreground">Used for reports and scheduling</p>
                      </div>
                    </div>
                    <select
                      value={workspaceConfig.timezone}
                      onChange={(e) => setWorkspaceConfig((p) => ({ ...p, timezone: e.target.value }))}
                      className="sm:w-48 px-3 py-2 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="UTC">UTC+0:00 (UTC)</option>
                      <option value="UTC+5:30">UTC+5:30 (IST)</option>
                      <option value="UTC-5">UTC-5:00 (EST)</option>
                      <option value="UTC-8">UTC-8:00 (PST)</option>
                      <option value="UTC+1">UTC+1:00 (CET)</option>
                      <option value="UTC+8">UTC+8:00 (SGT)</option>
                    </select>
                  </div>

                  {/* Language */}
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-success/10 border border-success/20 flex items-center justify-center flex-shrink-0">
                        <Globe size={15} className="text-success" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Platform Language</p>
                        <p className="text-xs text-muted-foreground">Interface language for all users</p>
                      </div>
                    </div>
                    <select
                      value={workspaceConfig.language}
                      onChange={(e) => setWorkspaceConfig((p) => ({ ...p, language: e.target.value }))}
                      className="sm:w-48 px-3 py-2 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="English">English</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="German">German</option>
                    </select>
                  </div>

                  {/* Notifications toggle */}
                  <div className="p-4 flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-warning/10 border border-warning/20 flex items-center justify-center flex-shrink-0">
                        <Bell size={15} className="text-warning" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Email Notifications</p>
                        <p className="text-xs text-muted-foreground">Alerts for tickets, invoices, and updates</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setWorkspaceConfig((p) => ({ ...p, notifications: !p.notifications }))}
                      className="flex-shrink-0"
                    >
                      {workspaceConfig.notifications ? (
                        <ToggleRight size={28} className="text-primary" />
                      ) : (
                        <ToggleLeft size={28} className="text-muted-foreground" />
                      )}
                    </button>
                  </div>

                  {/* Weekly reports toggle */}
                  <div className="p-4 flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <Settings2 size={15} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Weekly Summary Reports</p>
                        <p className="text-xs text-muted-foreground">Automated performance digest every Monday</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setWorkspaceConfig((p) => ({ ...p, weeklyReports: !p.weeklyReports }))}
                      className="flex-shrink-0"
                    >
                      {workspaceConfig.weeklyReports ? (
                        <ToggleRight size={28} className="text-primary" />
                      ) : (
                        <ToggleLeft size={28} className="text-muted-foreground" />
                      )}
                    </button>
                  </div>

                  {/* 2FA toggle */}
                  <div className="p-4 flex items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-success/10 border border-success/20 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck size={15} className="text-success" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
                        <p className="text-xs text-muted-foreground">Require 2FA for all admin accounts</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setWorkspaceConfig((p) => ({ ...p, twoFactor: !p.twoFactor }))}
                      className="flex-shrink-0"
                    >
                      {workspaceConfig.twoFactor ? (
                        <ToggleRight size={28} className="text-success" />
                      ) : (
                        <ToggleLeft size={28} className="text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 4: Permission Levels ── */}
            {currentStep === 4 && (
              <div className="space-y-5 animate-slide-up">
                <p className="text-sm text-muted-foreground">
                  Review and adjust module-level access for each role. Toggle permissions to match your organisation's security policy.
                </p>

                {/* Legend */}
                <div className="flex flex-wrap gap-3">
                  {[
                    { label: 'Admin', color: 'bg-amber-400/15 text-amber-400 border-amber-400/25' },
                    { label: 'Manager', color: 'bg-primary/15 text-primary border-primary/25' },
                    { label: 'Employee', color: 'bg-cyan/15 text-cyan border-cyan/25' },
                    { label: 'Client', color: 'bg-success/15 text-success border-success/25' },
                  ].map((item) => (
                    <span key={item.label} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${item.color}`}>
                      {item.label}
                    </span>
                  ))}
                </div>

                {/* Permissions table — desktop */}
                <div className="card-surface overflow-hidden hidden sm:block">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Module</th>
                          <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-widest text-amber-400">Admin</th>
                          <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-widest text-primary">Manager</th>
                          <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-widest text-cyan">Employee</th>
                          <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-widest text-success">Client</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {permissions.map((row, idx) => (
                          <tr key={row.module} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-3 text-sm text-foreground font-medium">{row.module}</td>
                            {(['admin', 'manager', 'employee', 'client'] as const).map((role) => (
                              <td key={role} className="px-3 py-3 text-center">
                                <button
                                  onClick={() => togglePermission(idx, role)}
                                  className={`w-6 h-6 rounded-md border flex items-center justify-center mx-auto transition-all duration-150 ${
                                    row[role]
                                      ? role === 'admin' ?'bg-amber-400/15 border-amber-400/40'
                                        : role === 'manager' ?'bg-primary/15 border-primary/40'
                                        : role === 'employee' ?'bg-cyan/15 border-cyan/40' :'bg-success/15 border-success/40' :'bg-muted border-border'
                                  }`}
                                >
                                  {row[role] && (
                                    <Check
                                      size={12}
                                      className={
                                        role === 'admin' ?'text-amber-400'
                                          : role === 'manager' ?'text-primary'
                                          : role === 'employee' ?'text-cyan' :'text-success'
                                      }
                                    />
                                  )}
                                </button>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Permissions — mobile cards */}
                <div className="sm:hidden space-y-3">
                  {permissions.map((row, idx) => (
                    <div key={row.module} className="card-surface p-4">
                      <p className="text-sm font-medium text-foreground mb-3">{row.module}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { role: 'admin' as const, label: 'Admin', color: 'text-amber-400', activeBg: 'bg-amber-400/10 border-amber-400/30' },
                          { role: 'manager' as const, label: 'Manager', color: 'text-primary', activeBg: 'bg-primary/10 border-primary/30' },
                          { role: 'employee' as const, label: 'Employee', color: 'text-cyan', activeBg: 'bg-cyan/10 border-cyan/30' },
                          { role: 'client' as const, label: 'Client', color: 'text-success', activeBg: 'bg-success/10 border-success/30' },
                        ]).map(({ role, label, color, activeBg }) => (
                          <button
                            key={role}
                            onClick={() => togglePermission(idx, role)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                              row[role] ? activeBg + ' ' + color : 'border-border bg-muted text-muted-foreground'
                            }`}
                          >
                            {row[role] ? <Check size={11} /> : <X size={11} />}
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-3.5 rounded-xl bg-success/5 border border-success/15 flex gap-3">
                  <Lock size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Permission changes take effect immediately. Fine-grained controls per user are available in <span className="text-foreground font-medium">Settings → Permissions</span>.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  currentStep === 1
                    ? 'opacity-30 cursor-not-allowed text-muted-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <ChevronLeft size={16} />
                Back
              </button>

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                  Continue
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  className="flex items-center gap-2 px-5 py-2.5 bg-success text-white text-sm font-semibold rounded-xl hover:bg-success/90 transition-all shadow-lg shadow-success/20"
                >
                  Go to Dashboard
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
